/**
 * V2QsSxCollectIntelligent 컴포넌트
 *
 * sx_collect_intelligent 타입 질문을 위한 체크박스 목록 컴포넌트
 * V1 QsInputSxIntelligentCollector의 V2 버전 (Phase 1: UI만)
 *
 * 기능:
 * - 증상 옵션들을 체크박스 목록으로 표시
 * - "None of the above" 옵션 특별 처리 (상호 배타적)
 * - 선택 시 + 아이콘 표시
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { TbCirclePlus } from 'react-icons/tb';
import { BiPencil } from 'react-icons/bi';
import { V2QsSxCollectIntelligentProps, V2QsSxCollectIntelligentValue } from './types';
import { usePatientVisitControllerSuggestSymptoms } from '../../../../api/generated/patient-visits/patient-visits';
import { questionControllerFindByKey } from '../../../../api/generated/questions/questions';
import LiveTestingSxSelectModal from '../../../../pages/survey/shared/live-testing/LiveTestingSxSelectModal';
import {
  useLiveTestingCollapsible,
  CollapsibleValue,
} from '../../../../pages/survey/shared/live-testing/useLiveTestingCollapsible';
import { useAssociatedSymptoms } from '../../../../pages/survey/shared/live-testing/useAssociatedSymptoms';
import type { LiveTestingAnswerValue } from '../../../../pages/survey/shared/live-testing/types';
import { useStaticLocales } from '../../../../hooks/use-static-locales';
import { usePatientTransData } from '../../../../hooks/use-patient-trans-data';
import useConfirmationDialog from '../../../../../hooks/use-confirmation-dialog';
import { useTranslations } from '../../../../../patient/hooks/use-translations';
import { getLocaleCode } from '../../../../pages/organization/configurations/language/constants';

const NONE_ABOVE_KEY = 'o-none-above';
const SX_SUGGEST_TTL_MS = 5 * 60 * 1000;
const PREFERRED_SX_COLLECT_KEYS = [
  'q-ros-collapsible',
  'q-ih-ros-collapsible',
  'q-ih-ped-ros-collapsible',
] as const;

interface SuggestSymptomsResult {
  symptomKeys: string[];
  symptomLabels: Record<string, string>;
}

const sxSuggestCache = new Map<string, { timestamp: number; data: SuggestSymptomsResult }>();
const sxSuggestInFlight = new Map<string, Promise<SuggestSymptomsResult>>();

function formatSymptomKeyLabel(key: string): string {
  if (!key) return '';

  const normalized = key
    .replace(/^sx-/, '')
    .replace(/^(ih|ped|ros)-/, '')
    .replace(/-/g, ' ')
    .trim();

  if (!normalized) return key;

  return normalized
    .split(' ')
    .map((word) => (word ? `${word.charAt(0).toUpperCase()}${word.slice(1)}` : ''))
    .join(' ');
}

function fallbackSymptomLabel(key: string): string {
  if (!key) return '';
  if (key.startsWith('sx-')) return formatSymptomKeyLabel(key);
  return key;
}

function V2QsSxCollectIntelligent({
  options,
  possibleSymptoms,
  answerKeys,
  answerOptionsMap,
  visitUuid,
  chiefComplaint,
  surveyId,
  locale,
  suppressSxCollectors = false,
  value,
  onChange,
  disabled = false,
  error = false,
  errorMessage,
  className,
}: V2QsSxCollectIntelligentProps) {
  const normalizedLocale = String(locale || 'english').toLowerCase();
  const isEnglishLocale = normalizedLocale === 'english';
  const [, { t }] = useTranslations({ locale: normalizedLocale as any });
  const [, { fetchTrans }] = usePatientTransData({
    keys: [],
    locale: normalizedLocale as any,
  });
  const { selectedKeys = [] } = value || {};
  const suggestSymptomsMutation = usePatientVisitControllerSuggestSymptoms();
  const [intelligentKeys, setIntelligentKeys] = useState<string[] | null>(null);
  const [intelligentLabels, setIntelligentLabels] = useState<Record<string, string>>({});
  const [pendingTranslationKeys, setPendingTranslationKeys] = useState<Record<string, boolean>>({});
  const [missingTranslationFallbackKeys, setMissingTranslationFallbackKeys] = useState<
    Record<string, boolean>
  >({});
  const [fetchedTranslationsByKey, setFetchedTranslationsByKey] = useState<Record<string, string>>(
    {},
  );
  const requestedTranslationKeysRef = useRef<Set<string>>(new Set());
  const normalizedChiefComplaint = (chiefComplaint || '').trim();
  // itemAnswers를 value에서 가져오거나 빈 객체 사용 (화면 이동 후에도 데이터 유지)
  const [itemAnswers, setItemAnswers] = useState<
    Record<string, Record<string, LiveTestingAnswerValue>>
  >(() => {
    return (value as any)?.itemAnswers || {};
  });
  const linkedQuestionKeys = useMemo(
    () => (answerKeys || []).filter((key): key is string => !!key && key.startsWith('q-')),
    [answerKeys],
  );
  const shouldResolvePossibleSymptoms = !!surveyId && linkedQuestionKeys.length > 0;
  const [resolvedPossibleSymptoms, setResolvedPossibleSymptoms] = useState<string[]>([]);
  const [isResolvingPossibleSymptoms, setIsResolvingPossibleSymptoms] = useState(false);
  const [isPossibleSymptomsResolved, setIsPossibleSymptomsResolved] = useState(
    () => !shouldResolvePossibleSymptoms,
  );

  // value prop이 변경되면 itemAnswers 동기화 (화면 이동 후 복귀 시)
  useEffect(() => {
    const valueItemAnswers = (value as any)?.itemAnswers;
    if (valueItemAnswers && Object.keys(valueItemAnswers).length > 0) {
      setItemAnswers(valueItemAnswers);
    }
  }, [value]);
  const { sltStr } = useStaticLocales({
    localeCode: getLocaleCode(normalizedLocale, 'en') as any,
  });
  const [confirmDialog, confirmationDialogUX] = useConfirmationDialog();

  const selectedSymptomTreeKeys = useMemo(
    () => selectedKeys.filter((key) => key && key.startsWith('sx-')),
    [selectedKeys],
  );
  // "None of the above"는 실제 증상(sx-*)이 선택된 경우에만 비활성화한다.
  const hasSymptomSelected = selectedSymptomTreeKeys.length > 0;
  const { associatedSymptomKeys } = useAssociatedSymptoms({
    selectedSymptomTreeKeys,
    surveyId,
    locale: normalizedLocale,
    enabled: !!surveyId && selectedSymptomTreeKeys.length > 0,
  });
  const hasAssociatedSymptoms = associatedSymptomKeys.length > 0;
  const syncHasSelectedSymptoms = hasSymptomSelected ? 'true' : 'false';
  const syncHasAssociatedSymptoms = hasAssociatedSymptoms ? 'true' : 'false';
  const baseUiMetadata = useMemo<Record<string, string>>(
    () => ({
      ...(((value as V2QsSxCollectIntelligentValue | undefined)?.uiMetadata || {}) as Record<
        string,
        string
      >),
    }),
    [value],
  );

  const buildNextValue = useCallback(
    (
      nextSelectedKeys: string[],
      nextItemAnswers: Record<string, Record<string, LiveTestingAnswerValue>>,
    ): V2QsSxCollectIntelligentValue => {
      const hasSelectedSymptoms = nextSelectedKeys.some((key) => key && key.startsWith('sx-'));
      return {
        selectedKeys: nextSelectedKeys,
        itemAnswers: nextItemAnswers as Record<string, Record<string, unknown>>,
        uiMetadata: {
          ...baseUiMetadata,
          hasSelectedSymptoms: hasSelectedSymptoms ? 'true' : 'false',
          // If no symptom tree is selected (e.g. only "none above"), associated symptoms must be false.
          hasAssociatedSymptoms: hasSelectedSymptoms ? syncHasAssociatedSymptoms : 'false',
        },
      };
    },
    [baseUiMetadata, syncHasAssociatedSymptoms],
  );

  const normalizedPossibleSymptoms = useMemo(() => {
    if (!possibleSymptoms || possibleSymptoms.length === 0) return [];
    return possibleSymptoms.filter((key) => key && key !== NONE_ABOVE_KEY && !key.startsWith('q-'));
  }, [possibleSymptoms]);

  useEffect(() => {
    let isActive = true;
    if (!shouldResolvePossibleSymptoms) {
      setResolvedPossibleSymptoms(normalizedPossibleSymptoms);
      setIsResolvingPossibleSymptoms(false);
      setIsPossibleSymptomsResolved(true);
      return () => {
        isActive = false;
      };
    }

    const resolvePossibleSymptoms = async () => {
      setIsPossibleSymptomsResolved(false);
      setIsResolvingPossibleSymptoms(true);
      try {
        const responses = await Promise.all(
          Array.from(new Set(linkedQuestionKeys)).map((questionKey) =>
            questionControllerFindByKey({
              surveyId,
              questionKey,
              locale: 'english',
            }).catch(() => null),
          ),
        );

        const sxCollectQuestions = responses.filter(
          (question): question is NonNullable<typeof question> =>
            !!question && question.inputType === 'sx_collect',
        );

        const preferredSxCollect = PREFERRED_SX_COLLECT_KEYS.map((preferredKey) =>
          sxCollectQuestions.find((question) => question.questionKey === preferredKey),
        ).find(Boolean);

        const selectedSxCollect = preferredSxCollect || sxCollectQuestions[0];

        const linkedSymptoms =
          selectedSxCollect?.answerKeys?.filter(
            (key): key is string => !!key && key !== NONE_ABOVE_KEY,
          ) || [];

        const nextPossibleSymptoms =
          linkedSymptoms.length > 0 ? linkedSymptoms : normalizedPossibleSymptoms;

        if (isActive) {
          setResolvedPossibleSymptoms(nextPossibleSymptoms);
        }
      } finally {
        if (isActive) {
          setIsResolvingPossibleSymptoms(false);
          setIsPossibleSymptomsResolved(true);
        }
      }
    };

    resolvePossibleSymptoms();

    return () => {
      isActive = false;
    };
  }, [linkedQuestionKeys, normalizedPossibleSymptoms, shouldResolvePossibleSymptoms, surveyId]);

  useEffect(() => {
    if (!visitUuid) return;
    setIntelligentKeys(null);
    setIntelligentLabels({});
  }, [normalizedChiefComplaint, visitUuid]);

  useEffect(() => {
    requestedTranslationKeysRef.current.clear();
    setPendingTranslationKeys({});
    setMissingTranslationFallbackKeys({});
    setFetchedTranslationsByKey({});
  }, [normalizedChiefComplaint, normalizedLocale, visitUuid]);

  const getMissingTranslationKeys = useCallback(
    (keysToCheck: string[]) => {
      if (isEnglishLocale) return [];
      return keysToCheck.filter((key) => {
        if (!key || key === NONE_ABOVE_KEY) return false;
        const translated = t(key, null, { fallbackValue: key }) || key;
        return translated === key;
      });
    },
    [isEnglishLocale, t],
  );

  const fetchMissingTranslations = useCallback(
    async (keysToCheck: string[]) => {
      const missingTranslationKeys = getMissingTranslationKeys(keysToCheck).filter((key) => {
        const localeScopedKey = `${normalizedLocale}:${key}`;
        if (requestedTranslationKeysRef.current.has(localeScopedKey)) {
          return false;
        }
        requestedTranslationKeysRef.current.add(localeScopedKey);
        return true;
      });

      if (missingTranslationKeys.length === 0) return;

      setPendingTranslationKeys((prev) => {
        const next = { ...prev };
        missingTranslationKeys.forEach((key) => {
          next[key] = true;
        });
        return next;
      });

      try {
        const response = await fetchTrans({ asNeededKeys: missingTranslationKeys });
        const responseRows =
          (response as any)?.translations ||
          (response as any)?.data?.translations?.data ||
          (response as any)?.translations?.data ||
          [];

        const fetchedMap = responseRows.reduce((acc: Record<string, string>, row: any) => {
          const source = row?.attributes || row;
          const key = source?.key;
          const translationValue = source?.value;
          if (
            typeof key === 'string' &&
            typeof translationValue === 'string' &&
            translationValue.trim().length > 0
          ) {
            acc[key] = translationValue;
          }
          return acc;
        }, {});

        if (Object.keys(fetchedMap).length > 0) {
          setFetchedTranslationsByKey((prev) => ({ ...prev, ...fetchedMap }));
        }

        setMissingTranslationFallbackKeys((prev) => {
          const next = { ...prev };
          missingTranslationKeys.forEach((key) => {
            const fetchedValue = fetchedMap[key];
            if (!fetchedValue || fetchedValue === key) {
              next[key] = true;
            } else {
              delete next[key];
            }
          });
          return next;
        });
      } catch {
        setMissingTranslationFallbackKeys((prev) => {
          const next = { ...prev };
          missingTranslationKeys.forEach((key) => {
            next[key] = true;
          });
          return next;
        });
      } finally {
        setPendingTranslationKeys((prev) => {
          const next = { ...prev };
          missingTranslationKeys.forEach((key) => {
            delete next[key];
          });
          return next;
        });
      }
    },
    [fetchTrans, getMissingTranslationKeys, normalizedLocale],
  );

  useEffect(() => {
    if (!intelligentKeys || intelligentKeys.length === 0) return;

    fetchMissingTranslations(intelligentKeys);
  }, [fetchMissingTranslations, intelligentKeys]);

  useEffect(() => {
    if (!selectedKeys || selectedKeys.length === 0) return;

    fetchMissingTranslations(selectedKeys);
  }, [fetchMissingTranslations, selectedKeys]);

  useEffect(() => {
    // suggest API가 빈 결과를 반환하면 fallback possible symptoms를 렌더링하므로
    // 이 키들도 별도로 번역 fetch를 트리거해야 라벨 공백이 발생하지 않는다.
    if (!visitUuid) return;
    if (intelligentKeys === null) return;
    if (intelligentKeys.length > 0) return;
    if (!resolvedPossibleSymptoms || resolvedPossibleSymptoms.length === 0) return;

    fetchMissingTranslations(resolvedPossibleSymptoms);
  }, [fetchMissingTranslations, intelligentKeys, resolvedPossibleSymptoms, visitUuid]);

  useEffect(() => {
    let isActive = true;
    // 백엔드에서 survey 기반으로 증상 목록을 자동 추출하므로 visitUuid만 있으면 호출 가능
    const shouldRun =
      !!visitUuid &&
      intelligentKeys === null &&
      !isResolvingPossibleSymptoms &&
      isPossibleSymptomsResolved;

    if (shouldRun) {
      const cacheKey = `${visitUuid}:${normalizedChiefComplaint}`;
      const now = Date.now();
      const cached = sxSuggestCache.get(cacheKey);
      if (cached && now - cached.timestamp < SX_SUGGEST_TTL_MS) {
        const applyCachedSuggestions = async () => {
          await fetchMissingTranslations(cached.data.symptomKeys);
          if (isActive) {
            setIntelligentKeys(cached.data.symptomKeys);
            setIntelligentLabels(cached.data.symptomLabels);
          }
        };
        applyCachedSuggestions();
      } else {
        const loadSuggestions = async () => {
          let inflight = sxSuggestInFlight.get(cacheKey);
          if (!inflight) {
            inflight = suggestSymptomsMutation
              .mutateAsync({
                data: {
                  uuid: visitUuid,
                  ...(normalizedChiefComplaint ? { cc: normalizedChiefComplaint } : {}),
                  ...(resolvedPossibleSymptoms.length > 0
                    ? { possibleSymptoms: resolvedPossibleSymptoms }
                    : {}),
                },
              } as any)
              .then((response: any) => {
                const symptomKeys = response?.symptomKeys || response?.data?.symptomKeys || [];
                const symptomLabels =
                  response?.symptomLabels || response?.data?.symptomLabels || {};
                const result: SuggestSymptomsResult = {
                  symptomKeys: Array.isArray(symptomKeys) ? symptomKeys : [],
                  symptomLabels: symptomLabels || {},
                };
                sxSuggestCache.set(cacheKey, { timestamp: Date.now(), data: result });
                return result;
              })
              .catch(() => {
                const emptyResult: SuggestSymptomsResult = { symptomKeys: [], symptomLabels: {} };
                sxSuggestCache.set(cacheKey, { timestamp: Date.now(), data: emptyResult });
                return emptyResult;
              })
              .finally(() => {
                sxSuggestInFlight.delete(cacheKey);
              });
            sxSuggestInFlight.set(cacheKey, inflight);
          }

          const result = await inflight;
          await fetchMissingTranslations(result.symptomKeys);
          if (isActive) {
            setIntelligentKeys(result.symptomKeys);
            setIntelligentLabels(result.symptomLabels);
          }
        };
        loadSuggestions();
      }
    }

    return () => {
      isActive = false;
    };
  }, [
    fetchMissingTranslations,
    intelligentKeys,
    isResolvingPossibleSymptoms,
    isPossibleSymptomsResolved,
    normalizedChiefComplaint,
    resolvedPossibleSymptoms,
    suggestSymptomsMutation,
    visitUuid,
  ]);

  const renderOptions = useMemo(() => {
    // API 호출 모드 (visitUuid가 있으면 백엔드에서 증상 목록 자동 추출)
    if (visitUuid) {
      // V1 parity: null = 로딩 중, [] = API 빈 결과 (fallback), [...] = API 결과
      const baseKeys = (() => {
        if (intelligentKeys === null) return []; // 로딩 중 → skeleton 표시
        if (intelligentKeys.length > 0) return intelligentKeys; // API 결과 있음
        // API 빈 결과 → fallback으로 normalizedPossibleSymptoms 사용 (있으면)
        return resolvedPossibleSymptoms.length > 0 ? resolvedPossibleSymptoms : [];
      })();
      // 이미 선택된 키는 항상 표시되어야 함 (API 결과에 없어도)
      const optionKeys = [
        // 선택된 항목 먼저 표시 (선택된 순서 유지)
        ...selectedKeys.filter((k) => k !== NONE_ABOVE_KEY),
        // 그 다음 baseKeys 중 아직 선택되지 않은 것들
        ...baseKeys.filter((k) => !selectedKeys.includes(k)),
        // None of the above는 마지막에
        ...(answerKeys?.includes(NONE_ABOVE_KEY) ? [NONE_ABOVE_KEY] : []),
      ];
      return optionKeys.map((key) => ({
        key,
        // 우선순위: 화면 라벨(answerOptionsMap) > 번역키 라벨 > API 라벨 > fallback
        label: (() => {
          const isSelectedKey = selectedKeys.includes(key);
          const optionLabel = answerOptionsMap?.[key]?.label as string | undefined;
          if (optionLabel) return optionLabel;

          const translated = t(key, null, { fallbackValue: key }) || key;
          if (translated && translated !== key) {
            return translated;
          }

          const fetchedTranslated = fetchedTranslationsByKey[key];
          if (fetchedTranslated && fetchedTranslated !== key) {
            return fetchedTranslated;
          }

          if (isEnglishLocale) {
            return intelligentLabels[key] || fallbackSymptomLabel(key);
          }

          if (pendingTranslationKeys[key]) {
            if (isSelectedKey) {
              return intelligentLabels[key] || key;
            }
            return '';
          }

          if (missingTranslationFallbackKeys[key]) {
            return key;
          }

          if (isSelectedKey) {
            return intelligentLabels[key] || key;
          }

          // Non-English locale: keep blank until translation fetch settles.
          return '';
        })(),
        isNoneAbove: key === NONE_ABOVE_KEY,
      }));
    }
    return options || [];
  }, [
    answerKeys,
    answerOptionsMap,
    fetchedTranslationsByKey,
    intelligentKeys,
    intelligentLabels,
    isEnglishLocale,
    missingTranslationFallbackKeys,
    resolvedPossibleSymptoms,
    options,
    pendingTranslationKeys,
    selectedKeys,
    t,
    visitUuid,
  ]);
  const isWaitingForSuggestions = !!visitUuid && intelligentKeys === null;
  const showSkeleton =
    !isWaitingForSuggestions && renderOptions.length === 0 && selectedKeys.length === 0;
  const labelByKey = useMemo(() => {
    const map = new Map<string, string>();
    renderOptions.forEach((option) => {
      map.set(option.key, option.label);
    });
    return map;
  }, [renderOptions]);
  const resolveSymptomName = useCallback(
    (key: string | null | undefined) => {
      const targetKey = key || '';
      if (!targetKey) return '';

      const mappedLabel = labelByKey.get(targetKey) || '';
      if (mappedLabel && mappedLabel !== targetKey) {
        return mappedLabel;
      }

      const optionLabel = (answerOptionsMap?.[targetKey]?.label as string | undefined) || '';
      if (optionLabel && optionLabel !== targetKey) {
        return optionLabel;
      }

      if (targetKey.startsWith('sx-')) {
        return formatSymptomKeyLabel(targetKey);
      }

      return mappedLabel || targetKey;
    },
    [answerOptionsMap, labelByKey],
  );

  const collapsibleOptionKeys = useMemo(
    () => renderOptions.map((option) => option.key),
    [renderOptions],
  );

  const collapsibleValue = useMemo<CollapsibleValue>(
    () => ({
      selectedKeys,
      itemAnswers,
    }),
    [itemAnswers, selectedKeys],
  );

  const handleCollapsibleChange = useCallback(
    (newValue: CollapsibleValue) => {
      setItemAnswers(newValue.itemAnswers || {});
      onChange?.(buildNextValue(newValue.selectedKeys, newValue.itemAnswers || {}));
    },
    [buildNextValue, onChange],
  );

  const { modalState, handleItemClick, handleModalClose, handleModalSave, updateModalAnswer } =
    useLiveTestingCollapsible({
      answerKeys: collapsibleOptionKeys,
      answerOptionsMap: answerOptionsMap || null,
      value: collapsibleValue,
      onChange: handleCollapsibleChange,
      locale: locale || 'english',
      surveyId,
      suppressSxCollectors,
    });

  // 증상 클릭 핸들러 - 모달만 열고, Save 시에만 체크됨
  const handleSymptomClick = useCallback(
    async (optionKey: string, isCurrentlySelected: boolean) => {
      if (!onChange) return;

      if (optionKey === NONE_ABOVE_KEY) {
        // "None of the above"는 바로 토글
        const newSelectedKeys = isCurrentlySelected ? [] : [NONE_ABOVE_KEY];
        onChange?.(buildNextValue(newSelectedKeys, isCurrentlySelected ? itemAnswers : {}));
        return;
      }

      if (isCurrentlySelected) {
        // 이미 선택된 경우: 모달 열어서 수정
        handleItemClick(optionKey, optionKey);
      } else {
        // 미선택인 경우: 모달만 열기 (Save 시 실제 체크됨)
        handleItemClick(optionKey, optionKey);
      }
    },
    [buildNextValue, handleItemClick, itemAnswers, onChange],
  );

  // 체크박스 해제 핸들러 (체크 해제만 처리)
  const handleUncheck = useCallback(
    async (optionKey: string) => {
      if (!onChange) return;

      if (optionKey === NONE_ABOVE_KEY) {
        onChange?.(buildNextValue([], itemAnswers));
        return;
      }

      const confirmed = await confirmDialog(
        '',
        sltStr({ key: 'msg-delete-answers' }),
        undefined,
        sltStr({ key: 'btn-delete-symptom' }),
      );
      if (!confirmed) return;

      // 일반 증상 선택 해제
      const newSelectedKeys = selectedKeys.filter((k) => k !== optionKey);
      const newItemAnswers = Object.fromEntries(
        Object.entries(itemAnswers || {}).filter(([key]) => key !== optionKey),
      );
      onChange?.(buildNextValue(newSelectedKeys, newItemAnswers));
      setItemAnswers(newItemAnswers);
    },
    [buildNextValue, confirmDialog, itemAnswers, onChange, selectedKeys, sltStr],
  );

  // 모달 저장 핸들러 - 저장 시 체크도 함께 처리
  const handleSaveAndCheck = useCallback(() => {
    const optionKey = modalState.selectedItemKey;
    if (optionKey && !selectedKeys.includes(optionKey) && optionKey !== NONE_ABOVE_KEY) {
      // None above 제거 후 추가
      const newSelectedKeys = [...selectedKeys.filter((k) => k !== NONE_ABOVE_KEY), optionKey];
      onChange?.(buildNextValue(newSelectedKeys, itemAnswers));
    }
    handleModalSave();
  }, [
    buildNextValue,
    handleModalSave,
    itemAnswers,
    modalState.selectedItemKey,
    onChange,
    selectedKeys,
  ]);

  // 모달에서 증상 삭제 핸들러
  const handleDeleteSymptom = useCallback(() => {
    const deletionTargets = [modalState.selectedItemKey, modalState.groupKey].filter(
      (key): key is string => !!key,
    );
    if (deletionTargets.length === 0) return;
    const deletionTargetSet = new Set(deletionTargets);

    // selectedKeys에서 제거
    const newSelectedKeys = selectedKeys.filter((k) => !deletionTargetSet.has(k));

    // itemAnswers에서도 제거
    const newItemAnswers = Object.fromEntries(
      Object.entries(itemAnswers || {}).filter(([key]) => !deletionTargetSet.has(key)),
    );

    onChange?.(buildNextValue(newSelectedKeys, newItemAnswers));
    setItemAnswers(newItemAnswers);

    // 모달 닫기
    handleModalClose();
  }, [
    buildNextValue,
    handleModalClose,
    itemAnswers,
    modalState.groupKey,
    modalState.selectedItemKey,
    onChange,
    selectedKeys,
  ]);

  useEffect(() => {
    if (!onChange) return;

    const currentHasSelectedSymptoms = baseUiMetadata.hasSelectedSymptoms;
    const currentHasAssociatedSymptoms = baseUiMetadata.hasAssociatedSymptoms;

    if (
      currentHasSelectedSymptoms === syncHasSelectedSymptoms &&
      currentHasAssociatedSymptoms === syncHasAssociatedSymptoms
    ) {
      return;
    }

    onChange(buildNextValue(selectedKeys, itemAnswers));
  }, [
    baseUiMetadata.hasAssociatedSymptoms,
    baseUiMetadata.hasSelectedSymptoms,
    buildNextValue,
    itemAnswers,
    onChange,
    selectedKeys,
    syncHasAssociatedSymptoms,
    syncHasSelectedSymptoms,
  ]);

  // 옵션 아이템 클래스
  const getOptionClasses = (isSelected: boolean, isDisabled: boolean) =>
    classNames(
      'flex items-center justify-between cursor-pointer transition-all duration-200',
      'border-[1.5px] px-4 py-3 rounded-lg',
      {
        'border-[#4B7BF5] bg-primary bg-opacity-5': isSelected && !isDisabled,
        'border-[#E6E9F5] bg-white': !isSelected && !isDisabled,
        'border-[#D0D4E4] bg-[#F8F9FC] cursor-default': isDisabled,
        'hover:border-[#DADDEB]': !isSelected && !isDisabled,
      },
    );

  // 체크박스 클래스
  const getCheckboxClasses = (isSelected: boolean, isDisabled: boolean) =>
    classNames('checkbox checkbox-lg rounded', {
      'checkbox-primary': !isDisabled,
      '!border-[2.5px] !border-[#9CA3B8]': isDisabled,
    });

  return (
    <div className={classNames('flex flex-col gap-2', className)}>
      {confirmationDialogUX}
      {isWaitingForSuggestions && (
        <div className='flex-1 flex items-center justify-center py-6'>
          <div className='flex flex-col items-center gap-2 text-sm text-[#737580]'>
            <span className='loading loading-spinner loading-md text-[#4B7BF5]' />
            {sltStr({ key: 'label-analyzing', capit: true })}
          </div>
        </div>
      )}
      {!isWaitingForSuggestions && showSkeleton && (
        <>
          <div className='h-[58px] rounded-lg bg-[#F2F4FA] animate-pulse' />
          <div className='h-[58px] rounded-lg bg-[#F2F4FA] animate-pulse' />
          <div className='h-[58px] rounded-lg bg-[#F2F4FA] animate-pulse' />
        </>
      )}
      {!isWaitingForSuggestions &&
        renderOptions.map((option) => {
          const isSelected = selectedKeys.includes(option.key);
          const isNoneAbove = option.key === NONE_ABOVE_KEY || !!option.isNoneAbove;
          // "None of the above"는 증상이 선택되면 비활성화
          const isOptionDisabled = disabled || (isNoneAbove && hasSymptomSelected);
          const hasAnswer = Boolean(itemAnswers?.[option.key]);

          return (
            <div
              key={option.key}
              className={getOptionClasses(isSelected, isOptionDisabled)}
              onClick={() => {
                if (isOptionDisabled) return;
                handleSymptomClick(option.key, isSelected);
              }}
            >
              <div className='flex items-center gap-3'>
                <input
                  type='checkbox'
                  checked={isSelected}
                  disabled={isOptionDisabled}
                  className={getCheckboxClasses(isSelected, isOptionDisabled)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    if (isOptionDisabled) return;
                    if (isSelected) {
                      // 체크 해제
                      handleUncheck(option.key);
                    } else {
                      // 체크하려면 모달 열기 (Save 시 체크됨)
                      handleSymptomClick(option.key, isSelected);
                    }
                  }}
                />
                <span className='text-base font-medium text-[#222638] text-left'>
                  {option.label}
                </span>
              </div>

              {/* + 아이콘 - "None of the above"가 아닌 경우에만 표시 */}
              {!isNoneAbove && (
                <div
                  className='p-0'
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isOptionDisabled) return;
                    handleSymptomClick(option.key, isSelected);
                  }}
                  aria-label='Open symptom details'
                >
                  {isSelected ? (
                    <BiPencil
                      size={22}
                      className={classNames('transition-opacity', {
                        'text-[#4B7BF5]': isSelected,
                        'text-[#AFB2C2]': !isSelected,
                        'opacity-50': isOptionDisabled,
                      })}
                    />
                  ) : (
                    <TbCirclePlus
                      size={22}
                      className={classNames('transition-opacity', {
                        'text-[#4B7BF5]': isSelected,
                        'text-[#AFB2C2]': !isSelected,
                        'opacity-50': isOptionDisabled,
                      })}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}

      <LiveTestingSxSelectModal
        isOpen={modalState.isOpen}
        symptomName={resolveSymptomName(modalState.selectedItemKey || modalState.groupKey)}
        questions={modalState.questions}
        answers={modalState.answers as Record<string, LiveTestingAnswerValue>}
        onAnswerChange={updateModalAnswer}
        onSave={handleSaveAndCheck}
        onClose={handleModalClose}
        onDelete={
          (modalState.selectedItemKey && selectedKeys.includes(modalState.selectedItemKey)) ||
          (modalState.groupKey && selectedKeys.includes(modalState.groupKey))
            ? handleDeleteSymptom
            : undefined
        }
        isLoading={modalState.isLoading}
        error={modalState.error}
        saveButtonText={sltStr({ key: 'btn-save', capit: true })}
        deleteButtonText={sltStr({ key: 'btn-delete-symptom' })}
      />

      {/* Error Message */}
      {error && errorMessage && <span className='text-xs text-[#FF5E71]'>{errorMessage}</span>}
    </div>
  );
}

export default memo(V2QsSxCollectIntelligent);
